import type { VirtualAppointmentWeeksFrom } from '../../services/virtualAppointments';

// Mirrors the backend's week trigger rules (virtualAppointments.due.ts); all days are UTC YYYY-MM-DD.
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_WEEK = 7;

export const DEFAULT_WEEKS_FROM: VirtualAppointmentWeeksFrom = 'treatment_start';

function dayTime(isoDay: string): number {
    return Date.parse(`${isoDay}T00:00:00.000Z`);
}

export function todayUtcIso(now: Date = new Date()): string {
    return now.toISOString().slice(0, 10);
}

/** The UTC day of an ISO instant, as the API counts an anchor; null when absent or invalid. */
export function utcDayOf(instant: string | null): string | null {
    if (instant === null) return null;
    const time = Date.parse(instant);
    return Number.isNaN(time) ? null : todayUtcIso(new Date(time));
}

export function weeksFromOf(triggerWeeksFrom: string | null): VirtualAppointmentWeeksFrom {
    return triggerWeeksFrom !== null ? 'now' : DEFAULT_WEEKS_FROM;
}

/** The day a saved week VA counts from: its own anchor when set, else the Treatment-phase start. */
export function weeksCountStart(
    treatmentStartDate: string | null,
    triggerWeeksFrom: string | null
): string | null {
    return utcDayOf(triggerWeeksFrom) ?? treatmentStartDate;
}

/** The day a weeks VA falls due, `weeks` after `countStart`; null without either. */
export function weeksDueDate(countStart: string | null, weeks: number | null): string | null {
    if (countStart === null || weeks === null) return null;
    const time = dayTime(countStart);
    if (Number.isNaN(time)) return null;
    return todayUtcIso(new Date(time + weeks * DAYS_PER_WEEK * MS_PER_DAY));
}

/** Whole weeks completed since `countStart` by `todayIso`; null without a start. */
export function currentTreatmentWeek(countStart: string | null, todayIso: string): number | null {
    if (countStart === null) return null;
    const elapsed = dayTime(todayIso) - dayTime(countStart);
    if (Number.isNaN(elapsed)) return null;
    return Math.max(0, Math.floor(elapsed / (DAYS_PER_WEEK * MS_PER_DAY)));
}

export function isDueDayReached(dueDate: string | null, todayIso: string): boolean {
    return dueDate !== null && dueDate <= todayIso;
}
